import { $t } from '~/i18n.js'

/**
 * Returns the full badge catalogue.
 *
 * Shape per entry:
 *   {
 *     label: string          — short translated name shown in UI
 *     description: string    — one-sentence description
 *     icon: string           — path to the PNG, served from /public/badges/
 *     levels: [              — ordered array; single-shot badges have 1 entry
 *       { level: numberstrin}
 *     ]
 *   }
 *
 * Icon files: /public/badges/<badge_name>.png  (designed separately)
 */
export function getBadgeConfig() {
    return {
        spl_welcome: {
            label: $t('Welkom'),
            description: $t('Eerste trainingsvlucht met een instructeur.'),
            icon: '/badges/welcome.png',
            levels: [$t('Voltooi je eerste trainingsvlucht')]
        },

        spl_solo: {
            label: $t('Solo'),
            description: $t('Je vliegt voor het allereerst alleen.'),
            icon: '/badges/solo.png',
            levels: [$t('Voltooi je eerste solovlucht')]
        },

        crosscountry: {
            label: $t('Overland'),
            description: $t('Je vliegt voor het eerst buiten vliegbereik van je veld.'),
            icon: '/badges/crosscountry.png',
            levels: [
                $t('Voltooi je eerste overlandvlucht met een instructeur'),
                $t('Voltooi een solo overlandvlucht')
            ]
        },

        launch_specialist: {
            label: $t('Startspecialist'),
            description: $t('Specialist in alle startmethodes, van lier naar zelfstart.'),
            icon: '/badges/launch_specialist.png',
            levels: [
                $t('${n} verschillende startmethodes', { n: 2 }),
                $t('${n} verschillende startmethodes', { n: 3 }),
                $t('${n} verschillende startmethodes', { n: 4 }),
                $t('${n} verschillende startmethodes', { n: 5 }),
            ]
        },

        starts: {
            label: $t('Starts'),
            description: $t('Totaal aantal starts in een kalenderjaar.'),
            icon: '/badges/starts.png',
            levels: [
                $t('${n} starts in een jaar', { n: 75 }),
                $t('${n} starts in een jaar', { n: 100 }),
                $t('${n} starts in een jaar', { n: 125 }),
                $t('${n} starts in een jaar', { n: 150 }),
            ],
        },

        thermal: {
            label: $t('Arend'),
            description: $t('Solo thermieken van toenemende duur.'),
            icon: '/badges/thermalling.png',
            levels: [
                $t('${n} minuten solo thermisch vliegen', { n: 15 }),
                $t('${n} minuten solo thermisch vliegen', { n: 30 }),
                $t('${n} uur solo thermisch vliegen', { n: 1 }),
                $t('${n} minuten solo thermisch vliegen', { n: 90 }),
                $t('${n} minuten solo thermisch vliegen', { n: 120 }),
            ],
        },


        evening_owl: {
            label: $t('Avonduil'),
            description: $t('Vertrek binnen 30 minuten vóór het einde van Universal Daylight Period (UDP).'),
            icon: '/badges/evening_owl.png',
            levels: [
                $t('Vertrek binnen 30 minuten vóór de UDP'),
            ],
        },

        morning_glory: {
            label: $t('Vroege vogel'),
            description: $t('Vertrek binnen 30 minuten na start Universal Daylight Period (UDP).'),
            icon: '/badges/morning_glory.png',
            levels: [
                $t('Vertrek binnen 30 minuten na de UDP'),
            ],
        },

        yin_yang: {
            label: $t('Yin-Yang'),
            description: $t('Gelijk balans tussen instructievluchten en solovluchten op dezelfde dag.'),
            icon: '/badges/yin_yang.png',
            levels: [
                $t('${dbo} instructievluchten en ${solo} solovluchten', { dbo: 2, solo: 2 }),
                $t('${dbo} instructievluchten en ${solo} solovluchten', { dbo: 3, solo: 3 }),
                $t('${dbo} instructievluchten en ${solo} solovluchten', { dbo: 4, solo: 4 }),
                $t('${dbo} instructievluchten en ${solo} solovluchten', { dbo: 5, solo: 5 }),
            ],
        },

        pedal_to_metal: {
            label: $t('Gas erop'),
            description: $t('Vlieg zoveel mogelijk starts op één dag.'),
            icon: '/badges/pedal_to_metal.png',
            levels: [
                $t('${n} starts op één dag', { n: 6 }),
                $t('${n} starts op één dag', { n: 9 }),
                $t('${n} starts op één dag', { n: 12 }),
                $t('${n} starts op één dag', { n: 15 }),
            ],
        },

        nomad: {
            label: $t('Nomade'),
            description: $t('Vlieg vanaf verschillende vliegvelden.'),
            icon: '/badges/nomad.png',
            levels: [
                $t('Gevlogen vanaf ${n} verschillende velden', { n: 2 }),
                $t('Gevlogen vanaf ${n} verschillende velden', { n: 3 }),
                $t('Gevlogen vanaf ${n} verschillende velden', { n: 4 }),
                $t('Gevlogen vanaf ${n} verschillende velden', { n: 5 }),
            ],
        },

        instructor_hopper: {
            label: $t('Instructeur Hopper'),
            description: $t('Vliegen met verschillende instructeurs.'),
            icon: '/badges/instructor_hopper.png',
            levels: [
                $t('Gevlogen met ${n} verschillende instructeurs', { n: 6 }),
                $t('Gevlogen met ${n} verschillende instructeurs', { n: 12 }),
                $t('Gevlogen met ${n} verschillende instructeurs', { n: 18 })
            ],
        },

        favourite_teacher: {
            label: $t('Favoriete instructeur'),
            description: $t('Opeenvolgende instructievluchten met dezelfde instructeur, mogen onderbroken worden door solovluchten.'),
            icon: '/badges/favourite_instructor.png',
            levels: [
                $t('${n} vluchten met één instructeur', { n: 5 }),
                $t('${n} vluchten met één instructeur', { n: 10 }),
                $t('${n} vluchten met één instructeur', { n: 15 }),
                $t('${n} vluchten met één instructeur', { n: 20 }),
            ],
        },

        aircraft: {
            label: $t('Vliegtuigspotter'),
            description: $t('Vlieg verschillende zweefvliegtuigtypes.'),
            icon: '/badges/aircraft.png',
            levels: [
                $t('Gevlogen met ${n} verschillende zweefvliegtuigen', { n: 2 }),
                $t('Gevlogen met ${n} verschillende zweefvliegtuigen', { n: 3 }),
                $t('Gevlogen met ${n} verschillende zweefvliegtuigen', { n: 4 }),
                $t('Gevlogen met ${n} verschillende zweefvliegtuigen', { n: 5 }),
            ],
        },

        consistency: {
            label: $t('Regelmaatsvlieger'),
            description: $t('Regelmatig vliegen binnen een kalendermaand.'),
            icon: '/badges/consistency.png',
            levels: [
                $t('${n} vliegdagen in een maand', { n: 5 }),
                $t('${n} vliegdagen in een maand', { n: 6 }),
                $t('${n} vliegdagen in een maand', { n: 7 }),
                $t('${n} vliegdagen in een maand', { n: 8 }),
            ],
        },

        weekend_warrior: {
            label: $t('Weekend Warrior'),
            description: $t('Geen enkel weekenddag missen in een kalendermaand.'),
            icon: '/badges/weekend_warrior.png',
            levels: [
                $t('Elke zaterdag in een kalendermaand'),
                $t('Elke zondag in een kalendermaand'),
                $t('Elk weekenddag (zaterdag én zondag) in een kalendermaand'),
            ],
        },

        sky_streak: {
            label: $t('Seizoensvlieger'),
            description: $t('Vlieg minstens één keer per maand, maand na maand.'),
            icon: '/badges/skystreak.png',
            levels: [
                $t('${n} maanden op rij gevlogen', { n: 6 }),
                $t('${n} maanden op rij gevlogen', { n: 12 }),
                $t('${n} maanden op rij gevlogen', { n: 18 }),
                $t('${n} maanden op rij gevlogen', { n: 24 }),
            ],
        },

    }
}



export const showHighestBadgePerLevel = (badges) => {
    const highestByName = {};
    for (const badge of badges) {
        if (!highestByName[badge.name] || badge.level > highestByName[badge.name].level) {
            highestByName[badge.name] = badge;
        }
    }
    badges = Object.values(highestByName);
    return badges;
}